import { useCallback, useRef } from "react";
import { Controller, useFormContext, type UseFormSetValue } from "react-hook-form";
import { useQuery, useLazyQuery } from "@apollo/client";
import { GET_EVENT_TYPES, GET_EVENT_DEFAULTS } from "@/pages/events/queries";
import Autocomplete from "@/components/Autocomplete";
import { EventType } from "@/pages/events/types";
import { useSetDefaultsCalled, useSetStepVisibility, type StepVisibilityMap } from "../FormVisibilityContext";
import { TERMS_CONTENT, buildSeminarSessions, getAvailableCustomFields, getGlobalMaxAttendance } from "../utils";
import type { EventDefaults, EventDefaultsQueryData } from "../types";
import type { FormValues } from "../validation/schema";
import { toLower, sortBy } from "lodash";
import { DateTime } from "luxon";

interface EventTypeQueryData {
  eventTypes: EventType[];
}

type SetValue = UseFormSetValue<FormValues>;

const toLocationType = (location: string): FormValues["locationType"] =>
  toLower(location) === "virtual" ? "virtual" : "onsite";

const toRegistrationOpenDate = (registrationOpenAt?: string) => {
  const parsedRegistrationOpenAt = registrationOpenAt ? DateTime.fromISO(registrationOpenAt, { zone: "utc" }) : null;

  return parsedRegistrationOpenAt?.isValid
    ? parsedRegistrationOpenAt.toFormat("yyyy-MM-dd")
    : DateTime.now().toFormat("yyyy-MM-dd");
};

function applyPATDefaults(setValue: SetValue, eventTypeName: string) {
  setValue("locationType", "onsite");
  setValue("registrationType", "all");
  setValue("customFields", Object.keys(getAvailableCustomFields(eventTypeName)));
  setValue("termsContent", TERMS_CONTENT);
}

function applyRecruitmentDefaults(setValue: SetValue) {
  setValue("registrationType", "all");
  setValue("customFields", []);
  setValue("termsContent", "");
}

export function hasConfiguredEventDefaults(defaults: EventDefaults | null | undefined): boolean {
  if (!defaults) return false;

  return (
    Object.keys(defaults.formVisibility ?? {}).length > 0 ||
    !!defaults.orderTemplateId ||
    !!defaults.location ||
    defaults.feeDefaults.length > 0
  );
}

function applyLegacyDefaults(
  setValue: SetValue,
  selected: EventType,
  setStepVisibility: (v: StepVisibilityMap) => void,
  setDefaultsCalled: (called: boolean) => void
) {
  if (selected.name === "PAT") applyPATDefaults(setValue, selected.name);
  else if (selected.name === "Recruitment") applyRecruitmentDefaults(setValue);

  setDefaultsCalled(false);
  setStepVisibility({});
  setValue("orderTemplateId", "");
  setValue("eventFees", []);
}

export function applyEventTypeDefaults(
  setValue: SetValue,
  selected: EventType,
  data: EventDefaultsQueryData | undefined,
  setStepVisibility: (v: StepVisibilityMap) => void,
  setDefaultsCalled: (called: boolean) => void
) {
  if (data?.eventDefaults && hasConfiguredEventDefaults(data.eventDefaults)) {
    setDefaultsCalled(true);
    applySeminarDefaults(setValue, data.eventDefaults, setStepVisibility);
  } else {
    applyLegacyDefaults(setValue, selected, setStepVisibility, setDefaultsCalled);
  }
}

export function applySeminarDefaults(
  setValue: SetValue,
  defaults: EventDefaults,
  setStepVisibility: (v: StepVisibilityMap) => void
) {
  // Event Details
  setValue("eventName", defaults.title);
  setValue("locationType", toLocationType(defaults.location));
  setValue("location", defaults.locationVirtualUrl ?? "");
  setValue("sectionId", defaults.section.id);
  setValue("section", defaults.section.name);
  setValue("legacyId", defaults.section.legacyId);

  if (defaults.locationFacility) {
    const { id: facilityId, name: facilityName, addresses } = defaults.locationFacility;

    setValue("facilityId", facilityId);
    setValue("facilityName", facilityName);
    setValue("facility", facilityName);

    const address = addresses[0];

    if (address) {
      setValue("address1", address.address1);
      setValue("address2", address.address2);
      setValue("city", address.city);
      setValue("state", address.state);
      setValue("zipcode", address.postalCode);
    }
  } else {
    setValue("facilityId", "");
    setValue("facilityName", "");
    setValue("facility", "");
    setValue("address1", "");
    setValue("address2", "");
    setValue("city", "");
    setValue("state", "");
    setValue("zipcode", "");
  }

  // Registration Options
  setValue("registrationType", "all");
  setValue("globalMaxAttendance", getGlobalMaxAttendance(buildSeminarSessions(defaults.sessions ?? [])));
  setValue("orderTemplateId", defaults.orderTemplateId ?? "");
  setValue("registrationOpenDate", {
    startDate: toRegistrationOpenDate(defaults.registrationOpenAt),
    endDate: toRegistrationOpenDate(defaults.registrationOpenAt)
  });
  setValue("waitlistType", defaults.enableWaitlist ? "notification" : "disabled");

  // Pricing is synced from the session-date-aware defaults query in SessionDetailsStep.
  setValue("eventFees", []);

  // Modules
  setValue("collectsAttendance", defaults.collectsAttendance);

  // Event Description
  setValue("description", defaults.description);

  // Step visibility
  setStepVisibility(defaults.formVisibility ?? {});
}

const EventTypesDropdown = ({ disabled }: { disabled: boolean }) => {
  const {
    control,
    clearErrors,
    trigger,
    setValue,
    formState: { errors }
  } = useFormContext<FormValues>();
  const setStepVisibility = useSetStepVisibility();
  const setDefaultsCalled = useSetDefaultsCalled();
  const selectedActivityTypeIdRef = useRef<string | null>(null);
  const { data: eventTypeData, loading } = useQuery<EventTypeQueryData>(GET_EVENT_TYPES);
  const [fetchEventDefaults] = useLazyQuery<EventDefaultsQueryData>(GET_EVENT_DEFAULTS);
  const eventTypes = sortBy(eventTypeData?.eventTypes ?? [], "name");

  const resetEventTypeDefaults = useCallback(() => {
    selectedActivityTypeIdRef.current = null;
    setDefaultsCalled(false);
    setStepVisibility({});
    setValue("orderTemplateId", "");
    setValue("eventFees", []);
  }, [setDefaultsCalled, setStepVisibility, setValue]);

  const applyDefaultsForSelection = useCallback(
    (selected: EventType, data?: EventDefaultsQueryData) => {
      if (selectedActivityTypeIdRef.current !== selected.id) return;

      applyEventTypeDefaults(setValue, selected, data, setStepVisibility, setDefaultsCalled);
    },
    [setValue, setStepVisibility, setDefaultsCalled]
  );

  const handleEventTypeSelect = useCallback(
    (id: string, onChange: (val: { id: string; name: string | undefined; source?: "activityType" }) => void) => {
      const selected = eventTypeData?.eventTypes.find((et) => et.id === id);

      if (selected) {
        selectedActivityTypeIdRef.current = selected.id;
        fetchEventDefaults({
          variables: { activityTypeId: selected.id },
          onCompleted: (data) => applyDefaultsForSelection(selected, data),
          onError: () => applyDefaultsForSelection(selected)
        });
      } else {
        resetEventTypeDefaults();
      }

      // GET_EVENT_TYPES returns Membership::ActivityType rows; always tag as activityType
      // so update/create submit activityTypeId instead of eventTypeId.
      onChange({ id, name: selected?.name, source: selected ? "activityType" : undefined });

      if (id && errors.eventType) clearErrors("eventType");
      if (!id) trigger("eventType");
    },
    [
      eventTypeData,
      applyDefaultsForSelection,
      fetchEventDefaults,
      errors.eventType,
      clearErrors,
      trigger,
      resetEventTypeDefaults
    ]
  );

  return (
    <Controller
      name="eventType"
      control={control}
      render={({ field: { onChange, onBlur, value } }) => {
        const selectedEventType = eventTypes.find((eventType) => eventType.id === value?.id);
        const query = selectedEventType?.name ?? value?.name ?? "";
        const filteredEventTypes = eventTypes.filter((eventType) =>
          eventType.name.toLowerCase().includes(query.toLowerCase())
        );

        return (
          <Autocomplete<EventType>
            data-testid="event-type-select"
            label="Event Type"
            placeholder="Select Event Type"
            data={filteredEventTypes}
            query={query}
            disabled={disabled || loading}
            loadingResults={loading}
            variant={disabled || loading ? "default" : "searchableDropdown"}
            required
            onChangeText={(text) => {
              onChange({ id: "", name: text });
              resetEventTypeDefaults();

              if (text && errors.eventType) clearErrors("eventType");
              if (!text) trigger("eventType");
            }}
            onBlur={() => {
              onBlur();

              if (query && !value?.id) {
                onChange({ id: "", name: "" });
                trigger("eventType");
              }
            }}
            onMouseDown={(e: MouseEvent) => {
              e.preventDefault();
            }}
            onSelect={(eventType) => handleEventTypeSelect(eventType.id, onChange)}
            reset={() => {
              onChange({ id: "", name: "" });
              resetEventTypeDefaults();
              trigger("eventType");
            }}
            error={!!errors.eventType}
            errorText={errors.eventType?.message as string}
            renderItem={(eventType) => <div className="text-body-large">{eventType.name}</div>}
          />
        );
      }}
    />
  );
};

export default EventTypesDropdown;
